/**
 * Specifications セクション。3 variants:
 *   key_value_table — 全項目をフラットに key-value 表で
 *   grouped         — グループ単位 (寸法 / 電源 / 性能 …) でまとめる
 *   inline_pills    — ピル状の小タグで横並び (短い項目向け)
 */
import { ListChecks } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { TextField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface SpecRow {
  label: string;
  value: string;
}
interface SpecGroup {
  groupTitle: string;
  rows: SpecRow[];
}
interface KeyValueConfig {
  title: string;
  rows: SpecRow[];
}
interface GroupedConfig {
  title: string;
  groups: SpecGroup[];
}
interface InlinePillsConfig {
  title: string;
  rows: SpecRow[];
}

function FlatEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as KeyValueConfig;
  const rows = cfg.rows ?? [];
  const set = (patch: Partial<KeyValueConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="タイトル"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <div className="space-y-1">
        <Label className="text-[11px]">スペック項目</Label>
        {rows.map((row, i) => (
          <div key={i} className="flex gap-1">
            <Input
              value={row.label}
              onChange={(e) => {
                const next = rows.slice();
                next[i] = { ...row, label: e.target.value };
                set({ rows: next });
              }}
              onBlur={onCommit}
              placeholder="項目名"
              className="text-xs flex-1"
            />
            <Input
              value={row.value}
              onChange={(e) => {
                const next = rows.slice();
                next[i] = { ...row, value: e.target.value };
                set({ rows: next });
              }}
              onBlur={onCommit}
              placeholder="値"
              className="text-xs flex-[2]"
            />
            <button
              type="button"
              onClick={() => set({ rows: rows.filter((_, idx) => idx !== i) })}
              className="text-red-500 px-1"
              aria-label="削除"
            >
              ×
            </button>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => set({ rows: [...rows, { label: '', value: '' }] })}
        >
          + 項目を追加
        </Button>
      </div>
    </div>
  );
}

function KeyValueRenderer({ config }: RendererProps) {
  const v = config as unknown as KeyValueConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <table className="w-full text-sm border border-[var(--border)]">
        <tbody>
          {(v.rows ?? []).map((r, i) => (
            <tr key={i} className="border-t border-[var(--border)]">
              <th className="p-3 text-left font-medium bg-[var(--bg-elevated)] w-1/3">
                {r.label}
              </th>
              <td className="p-3">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function GroupedEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as GroupedConfig;
  const groups = cfg.groups ?? [];
  const set = (patch: Partial<GroupedConfig>) =>
    onChange({ ...config, ...patch });

  return (
    <div className="space-y-3">
      <TextField
        label="タイトル"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      {groups.map((g, gi) => (
        <div
          key={gi}
          className="border border-[var(--border)] rounded p-2 space-y-2 bg-[var(--bg-elevated)]"
        >
          <div className="flex gap-1">
            <Input
              value={g.groupTitle}
              onChange={(e) => {
                const next = groups.slice();
                next[gi] = { ...g, groupTitle: e.target.value };
                set({ groups: next });
              }}
              onBlur={onCommit}
              placeholder="グループ名 (例: 寸法・重量)"
              className="text-xs"
            />
            <button
              type="button"
              onClick={() => set({ groups: groups.filter((_, i) => i !== gi) })}
              className="text-red-500 px-1"
              aria-label="グループ削除"
            >
              ×
            </button>
          </div>
          {(g.rows ?? []).map((row, ri) => (
            <div key={ri} className="flex gap-1">
              <Input
                value={row.label}
                onChange={(e) => {
                  const rows = g.rows.slice();
                  rows[ri] = { ...row, label: e.target.value };
                  const nextGroups = groups.slice();
                  nextGroups[gi] = { ...g, rows };
                  set({ groups: nextGroups });
                }}
                onBlur={onCommit}
                placeholder="項目"
                className="text-xs flex-1"
              />
              <Input
                value={row.value}
                onChange={(e) => {
                  const rows = g.rows.slice();
                  rows[ri] = { ...row, value: e.target.value };
                  const nextGroups = groups.slice();
                  nextGroups[gi] = { ...g, rows };
                  set({ groups: nextGroups });
                }}
                onBlur={onCommit}
                placeholder="値"
                className="text-xs flex-[2]"
              />
              <button
                type="button"
                onClick={() => {
                  const nextGroups = groups.slice();
                  nextGroups[gi] = {
                    ...g,
                    rows: g.rows.filter((_, idx) => idx !== ri),
                  };
                  set({ groups: nextGroups });
                }}
                className="text-red-500 px-1"
                aria-label="行削除"
              >
                ×
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const nextGroups = groups.slice();
              nextGroups[gi] = {
                ...g,
                rows: [...(g.rows ?? []), { label: '', value: '' }],
              };
              set({ groups: nextGroups });
            }}
          >
            + 項目を追加
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() =>
          set({ groups: [...groups, { groupTitle: '', rows: [] }] })
        }
      >
        + グループを追加
      </Button>
    </div>
  );
}

function GroupedRenderer({ config }: RendererProps) {
  const v = config as unknown as GroupedConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <div className="space-y-6">
        {(v.groups ?? []).map((g, gi) => (
          <div key={gi}>
            {g.groupTitle && (
              <div className="text-sm font-semibold mb-2 text-[var(--accent)]">
                {g.groupTitle}
              </div>
            )}
            <table className="w-full text-sm border border-[var(--border)]">
              <tbody>
                {(g.rows ?? []).map((r, i) => (
                  <tr key={i} className="border-t border-[var(--border)]">
                    <th className="p-3 text-left font-medium bg-[var(--bg-elevated)] w-1/3">
                      {r.label}
                    </th>
                    <td className="p-3">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

function InlinePillsRenderer({ config }: RendererProps) {
  const v = config as unknown as InlinePillsConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <div className="flex flex-wrap gap-2">
        {(v.rows ?? []).map((r, i) => (
          <div
            key={i}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] text-xs"
          >
            <span className="text-[var(--text-muted)]">{r.label}</span>
            <span className="font-semibold">{r.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const sampleFlat = (): Record<string, unknown> => ({
  title: '製品スペック',
  rows: [
    { label: '寸法', value: 'W120 × D60 × H80 mm' },
    { label: '重量', value: '1.4 kg' },
    { label: '電源', value: 'AC 100V / 50-60Hz' },
    { label: '消費電力', value: '15W' },
    { label: '保証期間', value: '12 ヶ月' },
  ] as SpecRow[],
});

const sampleGrouped = (): Record<string, unknown> => ({
  title: '詳細仕様',
  groups: [
    {
      groupTitle: '寸法・重量',
      rows: [
        { label: 'サイズ', value: 'W120 × D60 × H80 mm' },
        { label: '重量', value: '1.4 kg' },
      ],
    },
    {
      groupTitle: '電源',
      rows: [
        { label: '電源', value: 'AC 100V / 50-60Hz' },
        { label: '消費電力', value: '15W' },
      ],
    },
    {
      groupTitle: '性能',
      rows: [
        { label: '処理能力', value: '最大 200 件/分' },
        { label: '対応 OS', value: 'Windows / macOS / Linux' },
      ],
    },
  ] as SpecGroup[],
});

const samplePills = (): Record<string, unknown> => ({
  title: '主な特長',
  rows: [
    { label: 'サイズ', value: 'コンパクト' },
    { label: '重量', value: '1.4 kg' },
    { label: '保証', value: '12 ヶ月' },
    { label: '電源', value: 'AC 100V' },
  ] as SpecRow[],
});

export const specificationsDefinition: SectionDefinition = {
  type: 'specifications',
  label: '製品スペック',
  icon: ListChecks,
  description: '製品の仕様 (寸法・電源・性能等) を整理した表。',
  defaultVariant: 'key_value_table',
  useCases: ['physical_product', 'b2b_lease'],
  variants: {
    key_value_table: {
      label: 'シンプル表',
      description: 'フラットな key-value 表',
      Renderer: KeyValueRenderer,
      Editor: FlatEditor,
      sample: sampleFlat,
    },
    grouped: {
      label: 'グループ化',
      description: '寸法 / 電源 / 性能 などにまとめて表示',
      Renderer: GroupedRenderer,
      Editor: GroupedEditor,
      sample: sampleGrouped,
    },
    inline_pills: {
      label: 'ピル並列',
      description: '短いスペックをタグ状に並べる',
      Renderer: InlinePillsRenderer,
      Editor: FlatEditor,
      sample: samplePills,
    },
  },
};
